// src/components/admin/OrganizationSettingsForm.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import api from '../../utils/api';
import { showToast } from '../../utils/toast';

/**
 * OrganizationSettingsForm component provides a form to manage organization profile and branding
 * 
 * @param {Object} organization - Organization data object
 * @param {Function} onSettingsUpdated - Callback function when settings are successfully updated
 * @returns {JSX.Element} Rendered component
 */
const OrganizationSettingsForm = ({ organization, onSettingsUpdated }) => {
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    description: '',
    logo: '',
    primaryColor: '#2A1DA9',
    secondaryColor: '#5A4FC9',
    allowInternalTrading: true,
    allowExternalTrading: true,
    requireAdminApprovalForPerks: false,
    requireAdminApprovalForTrades: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Update form when organization data changes
  useEffect(() => {
    if (organization) {
      setSettingsForm({
        name: organization.name || '',
        description: organization.description || '',
        logo: organization.logo || '',
        primaryColor: organization.brandColors?.primary || '#2A1DA9',
        secondaryColor: organization.brandColors?.secondary || '#5A4FC9',
        allowInternalTrading: organization.tradingPolicies?.allowInternalTrading !== false,
        allowExternalTrading: organization.tradingPolicies?.allowExternalTrading !== false,
        requireAdminApprovalForPerks: organization.tradingPolicies?.requireAdminApprovalForPerks || false,
        requireAdminApprovalForTrades: organization.tradingPolicies?.requireAdminApprovalForTrades || false
      });
    }
  }, [organization]);
  
  // Handle form field changes
  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Validate color hex format
  const validateColorHex = (color) => {
    return /^#[0-9A-F]{6}$/i.test(color);
  };
  
  // Handle form submission
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!settingsForm.name.trim()) {
      showToast.error('Organization name is required');
      return;
    }
    
    if (!validateColorHex(settingsForm.primaryColor) || !validateColorHex(settingsForm.secondaryColor)) {
      showToast.error('Colors must be valid hex format (e.g. #1A2B3C)');
      return;
    }
    
    setSavingSettings(true);
    
    try {
      const updateData = {
        name: settingsForm.name,
        description: settingsForm.description,
        logo: settingsForm.logo,
        brandColors: {
          primary: settingsForm.primaryColor,
          secondary: settingsForm.secondaryColor
        },
        tradingPolicies: {
          allowInternalTrading: settingsForm.allowInternalTrading,
          allowExternalTrading: settingsForm.allowExternalTrading,
          requireAdminApprovalForPerks: settingsForm.requireAdminApprovalForPerks,
          requireAdminApprovalForTrades: settingsForm.requireAdminApprovalForTrades
        }
      };
      
      await api.put('/organizations', updateData);
      showToast.success('Organization settings updated successfully');
      
      // Fetch updated organization data
      const orgResponse = await api.get('/organizations');
      
      // Call the callback with updated data
      if (onSettingsUpdated && typeof onSettingsUpdated === 'function') {
        onSettingsUpdated(orgResponse.data);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  return (
    <form onSubmit={handleSaveSettings}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">Organization Settings</h2>
        <button 
          type="submit"
          disabled={savingSettings}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {savingSettings ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Organization Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Organization Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={settingsForm.name}
                  onChange={handleSettingsChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={settingsForm.description}
                  onChange={handleSettingsChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="orgDomain" className="block text-sm font-medium text-gray-700">
                  Domain
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    @
                  </span>
                  <input
                    type="text"
                    id="orgDomain"
                    value={organization?.domain || ''}
                    disabled={true}
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 bg-gray-50 text-gray-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {organization?.verificationStatus === 'verified'
                    ? 'Your domain is verified'
                    : 'Domain verification pending'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label htmlFor="logo" className="block text-sm font-medium text-gray-700">
                  Logo URL
                </label>
                <input
                  type="text"
                  id="logo"
                  name="logo"
                  value={settingsForm.logo}
                  onChange={handleSettingsChange}
                  placeholder="https://example.com/logo.png"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700">
                  Primary Color
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="color"
                    id="primaryColor"
                    name="primaryColor"
                    value={settingsForm.primaryColor}
                    onChange={handleSettingsChange}
                    className="h-8 w-8 p-0 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    name="primaryColor"
                    value={settingsForm.primaryColor}
                    onChange={handleSettingsChange}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="ml-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700">
                  Secondary Color
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="color"
                    id="secondaryColor"
                    name="secondaryColor"
                    value={settingsForm.secondaryColor}
                    onChange={handleSettingsChange}
                    className="h-8 w-8 p-0 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    name="secondaryColor"
                    value={settingsForm.secondaryColor}
                    onChange={handleSettingsChange}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="ml-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Trading Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Allow internal trading</h3>
                  <p className="text-xs text-gray-500">
                    Enable employees to trade perks with others in your organization
                  </p>
                </div>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input 
                    type="checkbox" 
                    id="allowInternalTrading"
                    name="allowInternalTrading"
                    checked={settingsForm.allowInternalTrading} 
                    onChange={handleSettingsChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label htmlFor="allowInternalTrading" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Allow external trading</h3>
                  <p className="text-xs text-gray-500">
                    Enable employees to trade perks with other companies
                  </p>
                </div>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input 
                    type="checkbox" 
                    id="allowExternalTrading"
                    name="allowExternalTrading"
                    checked={settingsForm.allowExternalTrading} 
                    onChange={handleSettingsChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label htmlFor="allowExternalTrading" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Require admin approval for perks</h3>
                  <p className="text-xs text-gray-500">
                    Admins must approve perks before they can be traded
                  </p>
                </div>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input 
                    type="checkbox" 
                    id="requireAdminApprovalForPerks"
                    name="requireAdminApprovalForPerks"
                    checked={settingsForm.requireAdminApprovalForPerks} 
                    onChange={handleSettingsChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label htmlFor="requireAdminApprovalForPerks" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Require admin approval for trades</h3>
                  <p className="text-xs text-gray-500">
                    Admins must approve trades before they can be completed
                  </p>
                </div>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input 
                    type="checkbox" 
                    id="requireAdminApprovalForTrades"
                    name="requireAdminApprovalForTrades"
                    checked={settingsForm.requireAdminApprovalForTrades} 
                    onChange={handleSettingsChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label htmlFor="requireAdminApprovalForTrades" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </form>
  );
};

OrganizationSettingsForm.propTypes = {
  organization: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    domain: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    verificationStatus: PropTypes.string,
    brandColors: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string
    }),
    tradingPolicies: PropTypes.shape({
      allowInternalTrading: PropTypes.bool,
      allowExternalTrading: PropTypes.bool,
      requireAdminApprovalForPerks: PropTypes.bool,
      requireAdminApprovalForTrades: PropTypes.bool
    })
  }),
  onSettingsUpdated: PropTypes.func
};

export default OrganizationSettingsForm;